import { z } from "zod";
import { parseContext } from "./context";
import { resolve, validateSubmission, type Context } from "./engine";
import type { Field, Project, Values } from "./schema";
import { isDynamicChoiceField } from "./dynamic-choices";

export const formProfileCollectionKindSchema = z.enum(["document", "library"]);
export type FormProfileCollectionKind = z.infer<
  typeof formProfileCollectionKindSchema
>;
export interface FormProfileLibrary {
  id: string;
  name: string;
  version: number;
}

export const formProfileNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .refine(
    (name) => !/[\u0000-\u001f\u007f]/.test(name),
    "Invalid profile name",
  );
const valuesSchema = z.record(
  z.string(),
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.record(z.string(), z.unknown()),
    z.null(),
  ]),
);
export const formProfileSchema = z
  .object({
    id: z.string().uuid(),
    name: formProfileNameSchema,
    revision: z.number().int().positive(),
    definitionHash: z.string().regex(/^[a-f0-9]{64}$/),
    versionId: z.string().max(200).optional(),
    values: valuesSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type FormProfile = z.infer<typeof formProfileSchema>;
export const formProfileInputSchema = z
  .object({
    id: z.string().uuid(),
    name: formProfileNameSchema,
    values: valuesSchema,
  })
  .strict();
export const formProfileUpdateSchema = formProfileInputSchema
  .omit({ id: true })
  .extend({ expectedRevision: z.number().int().positive() });
export const formProfileDeleteSchema = z
  .object({
    expectedRevision: z.number().int().positive(),
  })
  .strict();
export type FormProfileInput = z.input<typeof formProfileInputSchema>;
export type FormProfileUpdate = z.input<typeof formProfileUpdateSchema>;
export type FormProfileDelete = z.input<typeof formProfileDeleteSchema>;
export const formProfileRecipientSchema = z
  .string()
  .min(1)
  .max(200)
  .refine(
    (value) => value.trim() === value && !/[\u0000-\u001f\u007f]/.test(value),
  );
export const formProfileSharingSchema = formProfileDeleteSchema
  .extend({
    recipients: z
      .array(formProfileRecipientSchema)
      .max(50)
      .refine((values) => new Set(values).size === values.length),
  })
  .strict();
export type FormProfileSharing = z.input<typeof formProfileSharingSchema>;
export const sharedFormProfileSummarySchema = formProfileSchema
  .omit({ values: true, createdAt: true, versionId: true })
  .extend({ owner: formProfileRecipientSchema })
  .strict();
export type SharedFormProfileSummary = z.infer<
  typeof sharedFormProfileSummarySchema
>;
const collectionId = z.string().regex(/^[a-f0-9]{64}$/);
const sourceId = z.string().min(1).max(200);
export const formProfileCollectionSourceSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("document"),
      projectId: sourceId,
      formId: sourceId,
    })
    .strict(),
  z
    .object({ kind: z.literal("library"), libraryResourceId: sourceId })
    .strict(),
]);
export const ownedFormProfileSummarySchema = formProfileSchema
  .omit({ values: true, createdAt: true, versionId: true })
  .extend({ recipients: formProfileSharingSchema.shape.recipients })
  .strict();
export const formProfileCollectionSummarySchema = z
  .object({
    id: collectionId,
    source: formProfileCollectionSourceSchema,
    revision: z.number().int().positive(),
    profiles: z.array(ownedFormProfileSummarySchema).max(100),
  })
  .strict();
export const formProfileCatalogQuerySchema = z
  .object({
    cursor: collectionId.optional(),
    limit: z.number().int().min(1).max(20).default(10),
  })
  .strict();
export const formProfileCatalogSchema = z
  .object({
    durable: z.boolean(),
    collections: z.array(formProfileCollectionSummarySchema).max(20),
    nextCursor: collectionId.optional(),
  })
  .strict();
export const formProfileDataSchema = z
  .object({
    kind: z.literal("proof-form-profile"),
    version: z.literal(1),
    source: formProfileCollectionSourceSchema,
    profile: formProfileSchema,
  })
  .strict();
export const formProfileManageSchema = formProfileDeleteSchema
  .extend({
    name: formProfileNameSchema.optional(),
    revokeSharing: z.literal(true).optional(),
    removeRecipients: formProfileSharingSchema.shape.recipients
      .refine((ids) => ids.length > 0)
      .optional(),
  })
  .strict()
  .refine(
    (input) =>
      input.name !== undefined ||
      input.revokeSharing ||
      input.removeRecipients?.length,
  );
export type FormProfileCatalogQuery = z.input<
  typeof formProfileCatalogQuerySchema
>;
export type FormProfileCatalog = z.infer<typeof formProfileCatalogSchema>;
export type FormProfileData = z.infer<typeof formProfileDataSchema>;
export type FormProfileManage = z.input<typeof formProfileManageSchema>;

/** The reusable structure owns its inputs; instance mappings belong to the target document. */
export function formProfileDefinition(
  project: Project,
  formId: string,
): Project {
  const form = project.forms.find((item) => item.id === formId);
  if (!form) throw new Error("Form not found");
  return {
    ...project,
    fields: form.fields.map((field) => ({
      ...field,
      groupId: undefined,
      editable: formProfileWritable(field),
    })),
    groups: [],
    tabs: [],
    triggers: [],
    parts: [],
    formInstances: [],
    pages: project.pages.map((page) => ({ ...page, nodes: [] })),
  };
}
export const formProfileWritable = (field: Field) =>
  field.editable &&
  !field.system &&
  !field.autoGenerated &&
  !field.calculation &&
  !["lookup", "user", "order", "form", "dam"].includes(field.source.kind) &&
  (field.source.kind !== "dynamic" || isDynamicChoiceField(field));

/** Copy only JSON data, with the same depth/type limits as trusted context, before resolution. */
export function copyFormProfileValues(raw: unknown): Values {
  const copied = parseContext({ forms: { profile: raw } }).forms?.profile;
  if (
    !copied ||
    new TextEncoder().encode(JSON.stringify(copied)).length > 65536
  )
    throw new Error("Form profile exceeds 64 KB");
  return copied;
}
export function validateFormProfile(
  project: Project,
  formId: string,
  raw: unknown,
  context: Context = {},
) {
  const definition = formProfileDefinition(project, formId),
    input = copyFormProfileValues(raw),
    allowed = definition.fields.filter(formProfileWritable);
  if (
    Object.keys(input).some((key) => !allowed.some((field) => field.id === key))
  )
    throw new Error("Profile contains an unknown or protected form field");
  const result = validateSubmission(definition, input, undefined, context);
  // Derived and protected fields are recomputed when a profile is used, never frozen in storage.
  return {
    ...result,
    values: copyFormProfileValues(
      Object.fromEntries(
        allowed
          .filter(
            (field) =>
              result.resolution.visible.has(field.id) &&
              result.snapshot[field.id] !== undefined,
          )
          .map((field) => [field.id, result.snapshot[field.id]]),
      ),
    ),
  };
}

export function formProfileInputs(
  project: Project,
  formId: string,
  values: Values,
  context: Context = {},
) {
  const definition = formProfileDefinition(project, formId),
    resolution = resolve(definition, values, undefined, context);
  return copyFormProfileValues(
    Object.fromEntries(
      definition.fields
        .filter(
          (field) =>
            formProfileWritable(field) &&
            resolution.visible.has(field.id) &&
            resolution.values[field.id] !== undefined,
        )
        .map((field) => [field.id, resolution.values[field.id]]),
    ),
  );
}
